import type { PlayerGameSim } from "./types.ts";

export const pinchHitScore = (p: PlayerGameSim, powerSituation: boolean, contactSituation: boolean) => {
	const r = p.compositeRating;
	const weights = powerSituation ? [0.2, 0.65, 0.15] : contactSituation ? [0.6, 0.2, 0.2] : [0.4, 0.4, 0.2];
	return weights[0]! * r.contactHitter + weights[1]! * r.powerHitter + weights[2]! * r.eye;
};

// Evaluate every legal candidate before ranking. A weak glove needs an unused
// defender reserved for the next defensive half, rather than hoping one remains.
export const choosePinchHitter = (batter: PlayerGameSim, pos: string, bench: PlayerGameSim[], deficit: number, bases: boolean[], outs: number, allowCover = true) => {
	const power = deficit >= 2 && deficit <= bases.filter(Boolean).length + 1;
	const contact = !power && deficit <= 1 && bases[2] && outs < 2;
	const score = (p: PlayerGameSim) => pinchHitScore(p, power, Boolean(contact));
	const eligible = bench.filter(p => p.subIndex === undefined && !p.injured && (p.ptModifier ?? 1) > 0);
	const plans = eligible.flatMap(hitter => {
		if (score(hitter) < score(batter) + 0.1) return [];
		const fits = (p: PlayerGameSim) => (p.ovrs[pos as keyof typeof p.ovrs] ?? 0) >= (batter.ovrs[pos as keyof typeof batter.ovrs] ?? 0) - 10;
		const defender = pos === "DH" || fits(hitter) ? undefined : eligible.filter(p => p !== hitter && fits(p)).sort((a, b) => (b.ovrs[pos as keyof typeof b.ovrs] ?? 0) - (a.ovrs[pos as keyof typeof a.ovrs] ?? 0))[0];
		if (pos !== "DH" && !fits(hitter) && (!defender || !allowCover)) return [];
		// Never spend the last backup catcher on an unrelated hitting substitution.
		if (pos !== "C" && [hitter, defender].some(p => p && (p.ovrs.C ?? 0) >= 40) && !eligible.some(p => p !== hitter && p !== defender && (p.ovrs.C ?? 0) >= 40)) return [];
		return [{ hitter, defender, score: score(hitter) }];
	});
	const preferred = power ? plans.filter(p => p.hitter.powerPinchHitter) : [];
	return (preferred.length ? preferred : plans).sort((a, b) => b.score - a.score)[0];
};
